import { requestWithAbort, type RequestResponse } from '@atlassian/twg-common/request';

export type UserNoDataResponse = {
	status: 'NO_DATA';
};

export type BatchOutcome = 'fulfilled' | 'rejected' | 'skipped';

export type RelationshipsByProvider = Record<string, Record<string, number | null>>;

export type SharedWorkEntity = {
	ari: string;
	__typename?: string;
	displayName?: string | null;
	url?: string | null;
	thirdPartyId?: string;
};

export type UserMicrositeHeroMetricsResponse = {
	workByCollaborators?: Record<string, SharedWorkEntity[]>;
	relationshipsByProvider: RelationshipsByProvider;
	peopleWorkedWithCount: number | null;
	relationshipsCreated180d: number | string | null;
	computedAt: string;
	meta: {
		batchOutcomes: {
			relationshipsByProvider: BatchOutcome;
			peopleWorkedWith: BatchOutcome;
			relationshipsCreated180d: BatchOutcome;
		};
	};
};

export type UserMetricsGroup = 'fast' | 'medium' | 'slow';

export type UserMetricsResponse = UserMicrositeHeroMetricsResponse;

const MAX_TOP_COLLABORATORS = 5;

export const isUserNoDataResponse = (
	metrics: UserMicrositeHeroMetricsResponse | UserNoDataResponse | unknown,
): metrics is UserNoDataResponse =>
	metrics != null &&
	typeof metrics === 'object' &&
	(metrics as { status?: string }).status === 'NO_DATA';

export const requestUserMetrics = (
	orgId: string,
	cloudId: string,
	topCollaboratorAris?: ReadonlyArray<string>,
	groups?: ReadonlyArray<UserMetricsGroup>,
): {
	controller: AbortController;
	execute: Promise<RequestResponse<UserMicrositeHeroMetricsResponse | UserNoDataResponse>>;
} => {
	const headers: Record<string, string> = {
		'X-Query-Context': `ari:cloud:platform::site/${cloudId}`,
	};
	if (topCollaboratorAris && topCollaboratorAris.length > 0) {
		headers['X-Top-Collaborators'] = topCollaboratorAris.slice(0, MAX_TOP_COLLABORATORS).join(',');
	}

	const groupsParam =
		groups && groups.length > 0 ? `&groups=${Array.from(new Set(groups)).sort().join(',')}` : '';

	const { controller, execute } = requestWithAbort<
		UserMicrositeHeroMetricsResponse | UserNoDataResponse
	>(
		'GET',
		`/gateway/api/twg-api/v1/microsite/metrics/user?organisationId=${orgId}${groupsParam}`,
		null,
		headers,
		[200, 404],
	);
	return { controller, execute };
};
